import { DataTable, type DataTableColumn } from "@bruntworkwear-apps/ui";
import { money, quantity, signedMoney } from "@/shared/lib";
import type { ReceiptComparison } from "@/shared/api";
import styles from "./ComparisonTable.module.css";
import type { ComparisonTableProps } from "./ComparisonTable.types";

const customsVariance = (receipt: ReceiptComparison): number =>
  receipt.calculated_customs - receipt.booked_customs;

const landingVariance = (receipt: ReceiptComparison): number =>
  receipt.calculated_landing - receipt.booked_landing;

function buildColumns(threshold: number): DataTableColumn<ReceiptComparison>[] {
  return [
    { key: "receipt", header: "Item receipt", render: (r) => r.document_number },
    {
      key: "calc_customs",
      header: "Calc customs",
      render: (r) => money(r.calculated_customs),
      numeric: true,
    },
    {
      key: "booked_customs",
      header: "Booked customs",
      render: (r) => money(r.booked_customs),
      numeric: true,
    },
    {
      key: "customs_variance",
      header: "Customs variance",
      // The same rule the workbook applies: green inside the threshold because it
      // ties out, red beyond it because it needs an adjustment. Screen and export
      // cannot disagree.
      render: (r) => (
        <span className={Math.abs(customsVariance(r)) < threshold ? styles.tiesOut : styles.adjust}>
          {signedMoney(customsVariance(r))}
        </span>
      ),
      numeric: true,
    },
    {
      key: "calc_landing",
      header: "Calc landing",
      render: (r) => money(r.calculated_landing),
      numeric: true,
      secondary: true,
    },
    {
      key: "booked_landing",
      header: "Booked landing",
      render: (r) => money(r.booked_landing),
      numeric: true,
      secondary: true,
    },
    {
      key: "landing_variance",
      header: "Landing variance",
      render: (r) => signedMoney(landingVariance(r)),
      numeric: true,
    },
  ];
}

/** Calculated against what NetSuite already accrued, per item receipt. */
export function ComparisonTable({ receipts, threshold }: ComparisonTableProps) {
  const adjustable = receipts.filter((r) => Math.abs(customsVariance(r)) >= threshold);
  const totalCustoms = receipts.reduce((total, r) => total + customsVariance(r), 0);
  const totalLanding = receipts.reduce((total, r) => total + landingVariance(r), 0);

  return (
    <DataTable
      rows={receipts}
      columns={buildColumns(threshold)}
      rowKey={(receipt) => receipt.document_number}
      rowTone={(receipt) =>
        Math.abs(customsVariance(receipt)) >= threshold ? "warning" : "default"
      }
      maxHeight="34rem"
      emptyMessage="Nothing to compare."
      footer={
        <span className={styles.footer}>
          <span>{quantity(receipts.length)} receipts</span>
          <span>{quantity(adjustable.length)} to adjust</span>
          <span>Customs {signedMoney(totalCustoms)}</span>
          <span>Landing {signedMoney(totalLanding)}</span>
        </span>
      }
    />
  );
}
